import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { api } from '../api/client';
import { useAuthStore } from '../stores/auth.store';
import { NavBar } from '../components/NavBar';
import { toast } from '../stores/toast.store';

interface Dashboard {
  totals: { videos: number; published: number; queued: number; errors: number };
  last30days: Array<{ date: string; published: number; views: number }>;
  topVideos: Array<{
    publicationId: string; topicTitle: string; platform: string;
    views: number; likes: number; engagementRate: number;
    postUrl: string | null;
  }>;
  byPlatform: Array<{
    platform: string;
    totalViews: number; totalLikes: number;
    avgEngagementRate: number; publishedCount: number;
  }>;
}

const PLATFORM_COLOR: Record<string, string> = {
  instagram: '#e1306c',
  tiktok:    '#000000',
  youtube:   '#ff0000',
  linkedin:  '#0a66c2',
  telegram:  '#0088cc',
};

function StatCard({ label, value, icon, accent }: {
  label: string; value: number; icon: string;
  accent?: 'indigo' | 'green' | 'amber' | 'red';
}) {
  const accents = {
    indigo: 'from-indigo-500 to-violet-600',
    green:  'from-emerald-500 to-green-600',
    amber:  'from-amber-500 to-orange-600',
    red:    'from-rose-500 to-red-600',
  };
  return (
    <div className="card-glass card-glass-hover p-5">
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${accents[accent ?? 'indigo']} flex items-center justify-center text-white shadow-lg mb-3`}>
        {icon}
      </div>
      <div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div>
      <div className="text-3xl font-bold mt-1 tracking-tight">{value.toLocaleString('ru-RU')}</div>
    </div>
  );
}

export default function Analytics() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [data, setData] = useState<Dashboard | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = () => api.get('/analytics/dashboard').then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    if (!user) { navigate('/login', { replace: true }); return; }
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [user, navigate]);

  async function refresh() {
    setRefreshing(true);
    try {
      await api.post('/analytics/refresh');
      toast.info('Сбор метрик запущен');
      setTimeout(load, 3000);
    } finally {
      setTimeout(() => setRefreshing(false), 3000);
    }
  }

  if (!data) return (
    <div className="min-h-screen lg:pl-64">
      <NavBar />
    </div>
  );

  return (
    <div className="min-h-screen lg:pl-64">
      <NavBar />
      <div className="max-w-5xl mx-auto px-4 md:px-6 pt-6 pb-16">
        <header className="mb-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Аналитика</h1>
            <p className="text-slate-500 mt-1.5">Сбор метрик каждые 6 часов. Подробнее — по клику на «Обновить».</p>
          </div>
          <button onClick={refresh} disabled={refreshing} className="btn btn-md btn-ghost">
            {refreshing ? '⏳ Сбор…' : '⟳ Обновить метрики'}
          </button>
        </header>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard icon="🎬" label="Всего видео"   value={data.totals.videos}    accent="indigo" />
          <StatCard icon="🚀" label="Опубликовано"  value={data.totals.published} accent="green" />
          <StatCard icon="⏳" label="В очереди"     value={data.totals.queued}    accent="amber" />
          <StatCard icon="⚠"  label="Ошибки"        value={data.totals.errors}    accent="red" />
        </div>

        <section className="card-glass p-5 mb-6">
          <h2 className="text-sm font-semibold mb-4 text-slate-700">📈 Публикации и просмотры · 30 дней</h2>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.last30days}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v: string) => v.slice(5)} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e5e7eb', boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }} />
              <Legend />
              <Line yAxisId="left" type="monotone" dataKey="published" stroke="#6366f1" strokeWidth={2} name="Публикации" dot={{ r: 3 }} />
              <Line yAxisId="right" type="monotone" dataKey="views" stroke="#ec4899" strokeWidth={2} name="Просмотры" dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </section>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <section className="card-glass p-5">
            <h2 className="text-sm font-semibold mb-4 text-slate-700">🏆 Топ-5 видео</h2>
            {data.topVideos.length === 0 ? (
              <p className="text-sm text-slate-500">Нет данных. Нажми «Обновить метрики».</p>
            ) : (
              <ul className="space-y-2">
                {data.topVideos.map((v, i) => (
                  <li key={v.publicationId} className="flex items-center gap-3 py-2 border-b last:border-0 border-slate-100">
                    <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 text-white text-xs font-bold flex items-center justify-center shadow">
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{v.topicTitle}</div>
                      <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: PLATFORM_COLOR[v.platform] ?? '#888' }} />
                        <span>{v.platform}</span>
                        <span>·</span>
                        <span>ER {v.engagementRate.toFixed(2)}%</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold tabular-nums">{v.views.toLocaleString('ru-RU')}</div>
                      <div className="text-[10px] text-slate-400 uppercase">views</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="card-glass p-5">
            <h2 className="text-sm font-semibold mb-4 text-slate-700">📊 По платформам</h2>
            {data.byPlatform.length === 0 ? (
              <p className="text-sm text-slate-500">Нет данных.</p>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data.byPlatform}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="platform" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e5e7eb' }} />
                  <Legend />
                  <Bar dataKey="totalViews" name="Просмотры" fill="#6366f1" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="totalLikes" name="Лайки"     fill="#ec4899" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
